import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { Todo, TodoCreateData } from "../../../../types/types";
import { TodoListLocalRepository } from "./repository/TodoListRepository";
import { TodoLocalRepository } from "./repository/TodoRepository";

const useTodo_Local = (currentWorkspaceId: string | null, listRefresher: () => void) => {
    const [todos, setTodos] = useState<Todo[]>([]);
    const [todosMap, setTodosMap] = useState<Map<string, Todo>>(new Map());

    const todoRepository = useMemo(() => new TodoLocalRepository(), []);
    const todoListRepository = useMemo(() => new TodoListLocalRepository(), []);

    const createTodo = async (title?: string, listId?: string): Promise<Todo> => {
        if (!listId) throw new Error("No list selected");

        const newTodo: Todo = {
            id: crypto.randomUUID(),
            listId: listId,
            text: title ?? 'New Todo',
            completed: false,
            priority: 'low',
            createdAt: dayjs(),
            updatedAt: dayjs(),
        };

        await todoRepository.save(newTodo);

        // Update list's todos
        const list = await todoListRepository.findById(listId);
        if (list) {
            list.todos = [newTodo.id, ...list.todos];
            await todoListRepository.save(list);
        }

        await updatedRefresh();
        return newTodo;
    };

    const createTodoByTodoData = async (todoData: TodoCreateData, listId?: string): Promise<Todo> => {
        if (!listId) throw new Error("No list selected");

        const newTodo: Todo = {
            id: crypto.randomUUID(),
            listId: listId,
            text: todoData.text ?? '',
            completed: todoData.completed ?? false,
            priority: todoData.priority ?? 'low',
            description: todoData.description,
            dueDate: todoData.dueDate,
            location: todoData.location,
            createdAt: dayjs(),
            updatedAt: dayjs(),
        };

        await todoRepository.save(newTodo);

        const list = await todoListRepository.findById(listId);
        if (list) {
            list.todos = [newTodo.id, ...list.todos];
            await todoListRepository.save(list);
        }

        await updatedRefresh();
        return newTodo;
    };

    const getTodos = useCallback(async (): Promise<Todo[]> => {
        if (!currentWorkspaceId) return [];

        const workspaceLists = await todoListRepository.findAllByWorkspaceId(currentWorkspaceId);
        const listIds = workspaceLists.map(list => list.id);
        const allTodos = await todoRepository.findAll();
        return allTodos.filter(todo => listIds.includes(todo.listId ?? ""));
    }, [currentWorkspaceId, todoListRepository, todoRepository]);

    const updateTodo = async (todoId: string, updates: Partial<Todo>): Promise<Todo | null> => {
        const todo = await todoRepository.findById(todoId);
        if (!todo) return null;

        const updatedTodo = { ...todo, ...updates, updatedAt: dayjs() };
        await todoRepository.save(updatedTodo);
        await updatedRefresh();
        return updatedTodo;
    };

    const deleteTodo = async (todoId: string): Promise<boolean> => {
        const todo = await todoRepository.findById(todoId);
        if (!todo) return false;

        // Remove todo from list
        const list = await todoListRepository.findById(todo.listId ?? "");
        if (list) {
            list.todos = list.todos.filter(id => id !== todoId);
            await todoListRepository.save(list);
        }

        await todoRepository.delete(todoId);
        await updatedRefresh();
        return true;
    };

    const toggleTodo = async (todoId: string): Promise<boolean> => {
        const todo = await todoRepository.findById(todoId);
        if (!todo) return false;

        todo.completed = !todo.completed;
        todo.updatedAt = dayjs();
        await todoRepository.save(todo);
        await refresh();
        return true;
    };

    const moveTodoToList = async (
        todoId: string,
        sourceListId: string,
        destinationListId: string,
        destinationIndex: number
    ): Promise<boolean> => {
        const todo = await todoRepository.findById(todoId);
        if (!todo) return false;

        const reorderItem = (array: string[], from: number, to: number) => {
            const [movedTodo] = [...array.splice(from, 1)];
            array.splice(to, 0, movedTodo);
            return array;
        }

        // Move todo within the same list
        if (sourceListId === destinationListId) {
            const list = await todoListRepository.findById(sourceListId);
            if (!list) return false;

            const newTodos = reorderItem(list.todos, list.todos.indexOf(todoId), destinationIndex);
            list.todos = [...newTodos];
            await todoListRepository.save(list);
            await updatedRefresh();
            return true;
        }

        // Handle moving between different lists
        const sourceList = await todoListRepository.findById(sourceListId);
        const destinationList = await todoListRepository.findById(destinationListId);

        if (!sourceList || !destinationList) return false;

        // Remove from source list
        sourceList.todos = sourceList.todos.filter(id => id !== todoId);
        await todoListRepository.save(sourceList);

        // Add to destination list
        destinationList.todos = [...destinationList.todos];
        destinationList.todos.splice(destinationIndex, 0, todoId);
        await todoListRepository.save(destinationList);

        // Update todo
        todo.listId = destinationListId;
        todo.updatedAt = dayjs();
        await todoRepository.save(todo);

        await updatedRefresh();
        return true;
    };

    const refresh = useCallback(async () => {
        try {
            const fetchedTodos = await getTodos();
            setTodos(fetchedTodos);
            setTodosMap(new Map(fetchedTodos.map(todo => [todo.id, todo])));
        } catch (error) {
            // console.error("Error refreshing todos:");
        }
    }, [getTodos]);

    const updatedRefresh = useCallback(async () => {
        await refresh();
        listRefresher();
    }, [refresh, listRefresher]);

    const clear = useCallback(async () => {
        await todoRepository.clear();
        setTodos([]);
        setTodosMap(new Map());
    }, [todoRepository]);

    return {
        clear,
        refresh,
        todos,
        todosMap,
        createTodo,
        createTodoByTodoData,
        updateTodo,
        deleteTodo,
        toggleTodo,
        moveTodoToList,
    };
};

export default useTodo_Local;